/**
 * Turns the mapped shareholder structure into the graph the cards, the ELK layout, the phone list
 * and the Ultimate shareholders tables render.
 *
 * Pure (no React, no d3, no DOM). The input may be a MobX observable: it is detached with toJS and
 * only plain copies are changed, so toggling subsidiaries off and on again always starts from the
 * untouched source.
 *
 * Pipeline:
 *  1. detach and copy the nodes
 *  2. normalise the post-swap links to owner -> owned, re-derive subsidiary edges and merge
 *     parallel relationship rows into one edge with a shares array
 *  3. apply the share filter
 *  4. reclassify the copies when subsidiaries are hidden
 *  5. keep owners reachable upward from the target plus its direct subsidiaries
 *  6. aggregate and ultimate shareholders (before any collapse, so the General tab and the tables
 *     follow the filters only): the aggregate holds the ultimate owners the filters hide
 *  7. collapse '+N owners' (default: the owners of general-partner-only nodes; none with
 *     filters.expandGeneralPartners). Whether a node holds its stakes only as general partner is
 *     read from the unfiltered structure, so the share filter never changes it.
 *  8. back edges, levels and hop distances
 *  9. card models and a figure-independent topologyKey
 */
import { toJS } from 'mobx';
import { formatNumber } from '../../../../../utils/utils';
import {
  AGGREGATE_ID,
  AGGREGATE_NAME,
  GENERAL_PARTNER_SHARE,
  HIDDEN_OWNERS_CAUSE,
  NODE_KIND
} from './graphConstants';
import { filterEdgeShares, shareValue, sumShareBounds } from './graphFilters';
import { assignLevels, findBackEdges, shortestDistances } from './graphCycles';
import {
  buildAggregateCardModel,
  buildCardModel,
  formatEdgeShares,
  isGeneralPartnerOnly
} from './graphCardModel';

/** A node's graph id; the Ultimate shareholder(s) rows are the graph's own nodes, so theirs too. */
export const nodeIdOf = (node) => String(node.id ?? node.member_id);

// Largest beneficial ownership first; a bound counts with its value ('>50' as 50), an unknown
// value last. The sort is stable, so ties keep their input order.
const compareOwnershipDesc = (a, b) =>
  (shareValue(b.beneficial_ownership) ?? -1) - (shareValue(a.beneficial_ownership) ?? -1);

/**
 * Extract and sort ultimate shareholders from nodes
 * @param {Array} nodes - Array of node objects
 * @returns {Array} Sorted array of ultimate shareholders
 */
export const extractUltimateShareholders = (nodes) => {
  return nodes.filter((n) => n.is_beneficiary || n.is_ultimate).sort(compareOwnershipDesc);
};

// A link endpoint is an id; tolerate a node object in case a renderer resolved it in place.
const endpointId = (value) =>
  value !== null && typeof value === 'object' ? nodeIdOf(value) : String(value);

const groupBy = (items, keyOf) => {
  const groups = new Map();
  items.forEach((item) => {
    const key = keyOf(item);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(item);
  });
  return groups;
};

const compareIds = (a, b) => {
  if (a < b) return -1;
  return a > b ? 1 : 0;
};

/**
 * FE links are post-swap (endpoints.js): link.source = owned, link.target = owner. Rows for the
 * same owner and owned company (e.g. a GP role plus a share) become one edge
 * `${ownerId}->${ownedId}` whose shares keep the row order. link.is_subsidiary is never read (the
 * mapper sets it on the owner-direction link); an edge is a subsidiary edge when the target owns
 * a node the BE flagged is_subsidiary.
 */
export const normalizeEdges = (links, nodeById, targetId) => {
  const merged = new Map();
  links.forEach((link) => {
    const ownerId = endpointId(link.target);
    const ownedId = endpointId(link.source);
    if (ownerId === ownedId || !nodeById.has(ownerId) || !nodeById.has(ownedId)) return;
    const id = `${ownerId}->${ownedId}`;
    if (!merged.has(id)) {
      merged.set(id, {
        id,
        source: ownerId,
        target: ownedId,
        shares: [],
        isSubsidiary: ownerId === targetId && !!nodeById.get(ownedId).is_subsidiary
      });
    }
    merged.get(id).shares.push(link.share ?? null);
  });
  return [...merged.values()];
};

const applyShareFilter = (edges, filters) =>
  edges
    .map((edge) => ({ ...edge, shares: filterEdgeShares(edge, filters) }))
    .filter((edge) => edge.shares.length > 0);

// A hidden subsidiary that still owns into the target is an ordinary company owner, in the graph
// and in the ultimate list.
const reclassifyWithoutSubsidiaries = (copies) => {
  copies.forEach((node) => {
    if (!node.is_subsidiary) return;
    node.is_subsidiary = false;
    if (node.type === 'subsidiary') node.type = 'company';
  });
};

/** Owners reachable upward from the target (the target itself excluded). */
const collectOwners = (edges, targetId) => {
  const ownersOf = groupBy(
    edges.filter((edge) => !edge.isSubsidiary),
    (edge) => edge.target
  );
  const owners = new Set();
  const queue = [targetId];
  while (queue.length) {
    const nodeId = queue.shift();
    (ownersOf.get(nodeId) || []).forEach((edge) => {
      if (edge.source === targetId || owners.has(edge.source)) return;
      owners.add(edge.source);
      queue.push(edge.source);
    });
  }
  return owners;
};

// Direct subsidiaries that are not owners too (a circular company is placed once, as owner).
const collectSubsidiaries = (edges, targetId, ownerIds) =>
  new Set(
    edges
      .filter((edge) => edge.isSubsidiary && edge.source === targetId && !ownerIds.has(edge.target))
      .map((edge) => edge.target)
  );

const keepsEdge = (edge, targetId, ownerIds, subsidiaryIds) => {
  if (edge.isSubsidiary) return subsidiaryIds.has(edge.target) || ownerIds.has(edge.target);
  const ownerKept = edge.source === targetId || ownerIds.has(edge.source);
  return ownerKept && (edge.target === targetId || ownerIds.has(edge.target));
};

/** Owners reachable upward from the target plus its direct subsidiaries, and the edges between. */
export const pruneToTarget = (edges, targetId) => {
  const ownerIds = collectOwners(edges, targetId);
  const subsidiaryIds = collectSubsidiaries(edges, targetId, ownerIds);
  return {
    ownerIds,
    subsidiaryIds,
    edges: edges.filter((edge) => keepsEdge(edge, targetId, ownerIds, subsidiaryIds))
  };
};

// '9', '>54.5', '≥14.5' or '<9.5': a total in the BE's share notation.
const formatShareTotal = ({ op, value }) =>
  `${op === '=' ? '' : op}${formatNumber(value.toFixed(2))}`;

// Flagged ultimate owners, and for a legacy payload without the flags any node with a beneficial
// ownership. A node that sends a flag, even false, is never read by its beneficial ownership.
const isUltimateOwner = (node) => {
  if (node.is_beneficiary == null && node.is_ultimate == null) {
    return (shareValue(node.beneficial_ownership) ?? 0) > 0;
  }
  return !!(node.is_beneficiary || node.is_ultimate);
};

// Why owners are hidden: the share filter alone when lifting only it would show them all.
const hiddenCause = (ids, unfiltered) =>
  ids.every((id) => unfiltered.withoutShareFilter.has(id))
    ? HIDDEN_OWNERS_CAUSE.SHARE_FILTER
    : HIDDEN_OWNERS_CAUSE.FILTERS;

/**
 * The aggregate and the ultimate shareholder list. The aggregate holds the ultimate owners the
 * filters hide: owners linked to the target once the filters are lifted, so 'Show all' brings
 * each of them back (an owner with no path to the target at all is left out). Its total adds
 * their beneficial_ownership up with the bounds ('>50' and '4.5' give '>54.5'; null when no share
 * is known), its cause says which filters hide them. The ultimate list is
 * extractUltimateShareholders over [target, kept nodes in input order, aggregate].
 */
export const buildAggregateAndUltimates = (copies, target, keptIds, unfiltered) => {
  const hidden = copies.filter((node) => {
    const id = nodeIdOf(node);
    return !keptIds.has(id) && unfiltered.ownerIds.has(id) && isUltimateOwner(node);
  });
  const total = sumShareBounds(hidden.map((node) => node.beneficial_ownership));
  const aggregate = hidden.length
    ? {
        count: hidden.length,
        totalShare: total ? formatShareTotal(total) : null,
        cause: hiddenCause(hidden.map(nodeIdOf), unfiltered)
      }
    : null;
  const aggregateEntry = aggregate && {
    id: AGGREGATE_ID,
    member_name: AGGREGATE_NAME,
    beneficial_ownership: aggregate.totalShare,
    is_beneficiary: true,
    type: 'entity',
    age: ' '
  };
  const kept = [target, ...copies.filter((node) => node !== target && keptIds.has(nodeIdOf(node)))];
  const ultimateShareholders = extractUltimateShareholders(
    aggregateEntry ? [...kept, aggregateEntry] : kept
  ).map((node) => ({ ...node }));
  return { aggregate, aggregateEntry, ultimateShareholders };
};

// The distinct owners of a node. The target never counts: a circular holding without its
// subsidiary flag reads as the target owning the node, and the target is always on screen.
const ownerSourcesOf = (id, incomingById, targetId) =>
  new Set(
    (incomingById.get(id) || []).map((edge) => edge.source).filter((source) => source !== targetId)
  );

// The owners of a collapsed node that the collapse takes off the screen; owners that stay visible
// through another stake do not count.
const hiddenOwnerCountOf = (id, incomingById, visibleOwnerIds, targetId) =>
  [...ownerSourcesOf(id, incomingById, targetId)].filter((source) => !visibleOwnerIds.has(source))
    .length;

// The default collapse: the owners of general-partner-only nodes, none when the filters expand them.
const defaultCollapsedIds = (ownerIds, generalPartnerOnlyIds, filters) =>
  filters?.expandGeneralPartners ? [] : [...ownerIds].filter((id) => generalPartnerOnlyIds.has(id));

const resolveCollapsedIds = (
  requested,
  { ownerIds, targetId },
  incomingById,
  generalPartnerOnlyIds,
  filters
) => {
  const isCollapsible = (id) =>
    ownerIds.has(id) && ownerSourcesOf(id, incomingById, targetId).size > 0;
  const candidates =
    requested == null
      ? defaultCollapsedIds(ownerIds, generalPartnerOnlyIds, filters)
      : [...requested].map(String);
  return new Set(candidates.filter(isCollapsible));
};

/**
 * Hides the owners of collapsed nodes. A collapsed node keeps its own card and gets
 * hiddenOwnerCount, its owners that disappear with it; owners reachable another way stay.
 * Ultimates are not touched.
 */
export const applyCollapse = (edges, targetId, collapsedIds) => {
  const open = edges.filter((edge) => edge.isSubsidiary || !collapsedIds.has(edge.target));
  const ownerIds = collectOwners(open, targetId);
  const subsidiaryIds = collectSubsidiaries(open, targetId, ownerIds);
  return {
    ownerIds,
    subsidiaryIds,
    edges: open.filter((edge) => keepsEdge(edge, targetId, ownerIds, subsidiaryIds))
  };
};

const toNonSubsidiaryIncoming = (edges) =>
  groupBy(
    edges.filter((edge) => !edge.isSubsidiary),
    (edge) => edge.target
  );

// The owners on screen that hold a stake in a general-partner-only card, whether that stake is
// shown or behind the collapsed card.
const generalPartnerOwnerIdsOf = (generalPartnerOnlyIds, incomingById, targetId, visibleIds) =>
  [
    ...new Set(
      [...generalPartnerOnlyIds].flatMap((id) => [...ownerSourcesOf(id, incomingById, targetId)])
    )
  ]
    .filter((id) => visibleIds.has(id))
    .sort(compareIds);

// Why no owner is listed: none can be linked to the target at all, or the filters hide them all.
// null while an owner or the aggregate is shown.
const noOwnersCauseOf = (kept, aggregate, unfiltered) => {
  if (kept.ownerIds.size || aggregate) return null;
  if (!unfiltered.ownerIds.size) return HIDDEN_OWNERS_CAUSE.NONE_ON_RECORD;
  return hiddenCause([...unfiltered.ownerIds], unfiltered);
};

const aggregateEdge = (aggregate, targetId) => ({
  id: `${AGGREGATE_ID}->${targetId}`,
  source: AGGREGATE_ID,
  target: targetId,
  shares: [aggregate.totalShare],
  isSubsidiary: false,
  isAggregate: true
});

const kindOf = (id, targetId, visible) => {
  if (id === targetId) return NODE_KIND.TARGET;
  if (id === AGGREGATE_ID) return NODE_KIND.AGGREGATE;
  return visible.subsidiaryIds.has(id) ? NODE_KIND.SUBSIDIARY : NODE_KIND.OWNER;
};

const maxNumericShare = (edges = []) => {
  const values = edges
    .flatMap((edge) => edge.shares)
    .map(shareValue)
    .filter((value) => value !== null);
  return values.length ? Math.max(...values) : null;
};

// Structural order only (level, largest direct share, name, id): never figures, so the ELK input
// order is the same whether or not figures have arrived.
const compareStructurally = (a, b) =>
  a.level - b.level ||
  Number(a.kind === NODE_KIND.AGGREGATE) - Number(b.kind === NODE_KIND.AGGREGATE) ||
  (b.maxDirectShare ?? -1) - (a.maxDirectShare ?? -1) ||
  String(a.raw.member_name ?? '').localeCompare(String(b.raw.member_name ?? ''), 'de') ||
  compareIds(a.id, b.id);

const buildTopologyKey = (nodes, edges, collapsedIds) =>
  [
    nodes
      .map((node) => `${node.id}:${node.kind}:${node.level}`)
      .sort(compareIds)
      .join(','),
    edges
      .map((edge) => `${edge.id}${edge.isBackEdge ? '~' : ''}`)
      .sort(compareIds)
      .join(','),
    [...collapsedIds].sort(compareIds).join(',')
  ].join('|');

const buildCard = (node, ctx) => {
  if (node.kind === NODE_KIND.AGGREGATE) {
    return buildAggregateCardModel(ctx.aggregate, { percentageFilter: ctx.percentageFilter });
  }
  const isOwner = node.kind === NODE_KIND.OWNER;
  const outgoing = isOwner ? ctx.outgoingById.get(node.id) || [] : [];
  return buildCardModel(node.raw, {
    kind: node.kind,
    targetId: ctx.targetId,
    outgoing: outgoing.map((edge) => ({
      ...edge,
      equityToTarget: carriesEquityToTarget(edge, ctx)
    })),
    roleEdges: isOwner ? ctx.unfiltered.outgoingById.get(node.id) || [] : [],
    nodeById: ctx.nodeById,
    figuresByMemberId: ctx.figuresByMemberId,
    targetLocation: ctx.targetLocation
  });
};

const sortShareEdges = (node, ctx) => {
  if (node.kind === NODE_KIND.SUBSIDIARY) {
    return [ctx.edgeById.get(`${ctx.targetId}->${node.id}`)].filter(Boolean);
  }
  return node.kind === NODE_KIND.AGGREGATE ? [] : ctx.outgoingById.get(node.id);
};

const buildNodes = (ids, layout, ctx) =>
  ids.map((id) => {
    const raw = id === AGGREGATE_ID ? ctx.aggregateEntry : ctx.nodeById.get(id);
    const kind = kindOf(id, ctx.targetId, ctx.visible);
    // Only owners can be collapsed; the target and subsidiaries never hide anything.
    const ownerCount =
      kind === NODE_KIND.OWNER ? ownerSourcesOf(id, ctx.incomingById, ctx.targetId).size : 0;
    const isCollapsed = ctx.collapsedIds.has(id);
    const node = {
      id,
      memberId: id === AGGREGATE_ID ? AGGREGATE_ID : String(raw.member_id ?? id),
      kind,
      level: layout.levels.get(id),
      distance: layout.distances.get(id) ?? 1,
      inCycle: layout.cycleNodeIds.has(id),
      ownerCount,
      isCollapsed,
      hiddenOwnerCount: isCollapsed
        ? hiddenOwnerCountOf(id, ctx.incomingById, ctx.visible.ownerIds, ctx.targetId)
        : 0,
      raw
    };
    node.maxDirectShare = maxNumericShare(sortShareEdges(node, ctx));
    node.card = buildCard(node, ctx);
    return node;
  });

const buildEdges = (edges, backEdgeIds) =>
  edges
    .map((edge) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      shares: edge.shares,
      shareDisplay: formatEdgeShares(edge.shares),
      isBackEdge: backEdgeIds.has(edge.id),
      isSubsidiary: !!edge.isSubsidiary,
      isAggregate: !!edge.isAggregate
    }))
    .sort((a, b) => compareIds(a.id, b.id));

const layoutGraph = (ids, edges, targetId, subsidiaryIds) => {
  const { backEdgeIds, cycleNodeIds } = findBackEdges(ids, edges, targetId);
  return {
    backEdgeIds,
    cycleNodeIds,
    levels: assignLevels(ids, edges, targetId, { backEdgeIds, subsidiaryIds }),
    distances: shortestDistances(targetId, edges)
  };
};

const levelRange = (nodes) => {
  const levels = nodes.map((node) => node.level);
  return { min: Math.min(...levels), max: Math.max(...levels) };
};

// Steps 1-4: detached copies, merged and filtered owner -> owned edges.
const readStructure = (input, filters) => {
  const structure = toJS(input);
  if (!structure?.nodes?.length || !structure?.links?.length) return null;
  const copies = structure.nodes.map((node) => ({ ...node }));
  const target = copies.find((node) => node.target);
  if (!target) return null;
  const targetId = nodeIdOf(target);
  const nodeById = new Map(copies.map((node) => [nodeIdOf(node), node]));
  const allEdges = normalizeEdges(structure.links, nodeById, targetId);
  const edges = applyShareFilter(allEdges, filters);
  if (!filters?.includeSubsidiaries) reclassifyWithoutSubsidiaries(copies);
  return { copies, target, targetId, nodeById, allEdges, edges };
};

// Any share other than 'GP' may carry equity; an unknown one too.
const hasEquityShare = (edge) => edge.shares.some((share) => share !== GENERAL_PARTNER_SHARE);

/**
 * The structure without any filter: the owners that can be linked to the target at all and every
 * stake each of them holds. Roles read from here (general partner only, equity reaching the
 * target) never change with a display filter.
 */
const readUnfiltered = (source, filters) => {
  const linked = pruneToTarget(source.allEdges, source.targetId);
  const ownerEdges = linked.edges.filter((edge) => !edge.isSubsidiary);
  const outgoingById = groupBy(ownerEdges, (edge) => edge.source);
  return {
    ownerIds: linked.ownerIds,
    // The owners shown with only the share filter lifted (0 % keeps every number).
    withoutShareFilter: filters?.includeUnknownShares
      ? linked.ownerIds
      : collectOwners(
          applyShareFilter(source.allEdges, { ...filters, percentageFilter: 0 }),
          source.targetId
        ),
    outgoingById,
    edgeById: new Map(ownerEdges.map((edge) => [edge.id, edge])),
    generalPartnerOnlyIds: new Set(
      [...linked.ownerIds].filter((id) => isGeneralPartnerOnly(outgoingById.get(id)))
    ),
    // Owners whose stakes reach the target through equity; a general partner company holding
    // only 'GP' passes none on.
    equityOwnerIds: collectOwners(ownerEdges.filter(hasEquityShare), source.targetId)
  };
};

// Whether equity reaches the target through this holding: the stake itself (unfiltered) carries
// equity, into the target or into an owner that passes equity on.
const carriesEquityToTarget = (edge, ctx) => {
  const unfilteredEdge = ctx.unfiltered.edgeById.get(edge.id) ?? edge;
  return (
    hasEquityShare(unfilteredEdge) &&
    (edge.target === ctx.targetId || ctx.unfiltered.equityOwnerIds.has(edge.target))
  );
};

/**
 * The default collapse keeps only the cards whose collapse hides an owner: a general partner whose
 * owners all stay on screen through stakes of their own (the limited partners of a KG who also
 * own its general partner) is shown expanded. Dropping such a card changes no visible owner, so
 * one pass is enough. A card hidden behind another collapsed card keeps its default.
 */
const collapseHidingOwners = (keptEdges, targetId, collapsedIds, incomingById) => {
  const visible = applyCollapse(keptEdges, targetId, collapsedIds);
  const hidesOwner = (id) =>
    !visible.ownerIds.has(id) ||
    hiddenOwnerCountOf(id, incomingById, visible.ownerIds, targetId) > 0;
  const hiding = new Set([...collapsedIds].filter(hidesOwner));
  if (hiding.size === collapsedIds.size) return { collapsedIds, visible };
  return { collapsedIds: hiding, visible: applyCollapse(keptEdges, targetId, hiding) };
};

// Steps 5-7: prune, ultimates/aggregate, collapse.
const selectVisible = (source, filters, options) => {
  const unfiltered = readUnfiltered(source, filters);
  const kept = pruneToTarget(source.edges, source.targetId);
  const keptIds = new Set([source.targetId, ...kept.ownerIds, ...kept.subsidiaryIds]);
  const totals = buildAggregateAndUltimates(source.copies, source.target, keptIds, unfiltered);
  const incomingById = toNonSubsidiaryIncoming(kept.edges);
  const outgoingById = groupBy(
    kept.edges.filter((edge) => !edge.isSubsidiary),
    (edge) => edge.source
  );
  const requested = resolveCollapsedIds(
    options.collapsedNodeIds,
    { ownerIds: kept.ownerIds, targetId: source.targetId },
    incomingById,
    unfiltered.generalPartnerOnlyIds,
    filters
  );
  // An explicit collapse set (a click) is kept as it is.
  const { collapsedIds, visible } =
    options.collapsedNodeIds == null
      ? collapseHidingOwners(kept.edges, source.targetId, requested, incomingById)
      : {
          collapsedIds: requested,
          visible: applyCollapse(kept.edges, source.targetId, requested)
        };
  return { unfiltered, kept, totals, incomingById, outgoingById, collapsedIds, visible };
};

/**
 * @param {Object} input - { nodes, links } from the mapper (may be observable)
 * @param {{percentageFilter: number, includeUnknownShares: boolean, includeSubsidiaries: boolean,
 *   expandGeneralPartners?: boolean}} filters
 * @param {Object} [options]
 * @param {Map<string, Object>} [options.figuresByMemberId] - NodeFigures by String(member_id)
 * @param {Iterable<string>|null} [options.collapsedNodeIds] - null = the default of the filters
 * @param {{city, country}} [options.targetLocation] - fallback for the target card
 * @returns {Object|null} PreparedGraph, or null without a target node or links.
 *   collapsedGeneralPartnerCount: the visible general-partner-only cards whose collapse hides an
 *   owner (a card hidden behind another collapsed card is not counted)
 *   generalPartnerOwnerIds: the owners on screen that hold a stake in a general-partner-only card
 *   hiddenUltimateCount: the ultimate owners the collapse hides (the table view's note)
 *   noOwnersCause: why no owner is listed (a HIDDEN_OWNERS_CAUSE), null while an owner or the
 *   aggregate is shown
 */
export const prepareShareholderGraph = (input, filters, options = {}) => {
  const source = readStructure(input, filters);
  if (!source) return null;
  const { targetId } = source;
  const { unfiltered, kept, totals, incomingById, outgoingById, collapsedIds, visible } =
    selectVisible(source, filters, options);

  const ids = [targetId, ...visible.ownerIds, ...visible.subsidiaryIds];
  const edges = [...visible.edges];
  if (totals.aggregate) {
    ids.push(AGGREGATE_ID);
    edges.push(aggregateEdge(totals.aggregate, targetId));
  }
  const visibleIds = new Set(ids);
  const layout = layoutGraph(ids, edges, targetId, visible.subsidiaryIds);

  const ctx = {
    targetId,
    visible,
    collapsedIds,
    incomingById,
    outgoingById,
    unfiltered,
    edgeById: new Map(kept.edges.map((edge) => [edge.id, edge])),
    nodeById: source.nodeById,
    aggregate: totals.aggregate,
    aggregateEntry: totals.aggregateEntry,
    figuresByMemberId: options.figuresByMemberId,
    targetLocation: options.targetLocation,
    percentageFilter: filters?.percentageFilter
  };
  const nodes = buildNodes(ids, layout, ctx).sort(compareStructurally);
  const graphEdges = buildEdges(edges, layout.backEdgeIds);
  const collapsedGeneralPartnerCount = nodes.filter(
    (node) => node.hiddenOwnerCount > 0 && unfiltered.generalPartnerOnlyIds.has(node.id)
  ).length;

  return {
    targetId,
    nodes,
    edges: graphEdges,
    ultimateShareholders: totals.ultimateShareholders,
    aggregate: totals.aggregate,
    counts: { owners: kept.ownerIds.size, subsidiaries: visible.subsidiaryIds.size },
    levels: levelRange(nodes),
    noOwnersCause: noOwnersCauseOf(kept, totals.aggregate, unfiltered),
    collapsedNodeIds: [...collapsedIds].sort(compareIds),
    collapsedGeneralPartnerCount,
    generalPartnerOwnerIds: generalPartnerOwnerIdsOf(
      unfiltered.generalPartnerOnlyIds,
      incomingById,
      targetId,
      visibleIds
    ),
    hiddenUltimateCount: totals.ultimateShareholders.filter(
      (entry) => !visibleIds.has(nodeIdOf(entry))
    ).length,
    topologyKey: buildTopologyKey(nodes, graphEdges, collapsedIds)
  };
};

/**
 * The collapse set after toggling one node, starting from what the graph currently shows (the
 * default rule of the filters included), so the first click on a default-collapsed node expands
 * it.
 * @returns {Set<string>}
 */
export const toggleCollapsedNodeId = (prepared, nodeId) => {
  const next = new Set(prepared?.collapsedNodeIds || []);
  const id = String(nodeId);
  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }
  return next;
};
